/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { AbstractDataService, LoggerService } from '@pf/shared-services';
import {
    ContainerViewModelRead,
    EquipmentService,
} from '@control-tower/platform-wms';
import {
    EntityChangeTrackingDto,
    IChangesSearchParams,
    IPagedResult,
    PFWmsEntities,
} from '@pf/shared-common';
import { EquipmentStore } from './equipment.store';
import {
    EquipmentCreate,
    EquipmentRead,
    EquipmentSearchParams,
} from '../../entities/equipment.dto';
import { Operation } from 'fast-json-patch';
import { ContainerStore } from '../containers/container.store';
import { ContainerRead } from '../../entities/container.dto';
import { asArraySafe } from '@pf/shared-utility';

@Injectable()
export class EquipmentDataService extends AbstractDataService<
    EquipmentRead,
    EquipmentCreate
> {
    private containerStore = inject(ContainerStore);
    protected EntityName = PFWmsEntities.Equipment;
    constructor(
        private service: EquipmentService,
        store: EquipmentStore,
        logger: LoggerService
    ) {
        super(store, logger);
    }
    protected createObs$(body: EquipmentCreate): Observable<EquipmentRead> {
        return this.service
            .equipmentCreate({
                equipmentViewModelCreateBody: body,
            })
            .pipe(
                map(result => result as EquipmentRead),
                tap(result => {
                    this.addContainersToStoreIfNotPresent(
                        asArraySafe(result.containers)
                    );
                })
            );
    }
    protected deleteObs$(id: string): Observable<EquipmentRead> {
        return this.service.equipmentDelete({
            id,
        }) as Observable<EquipmentRead>;
    }
    protected undoDeleteObs$(id: string): Observable<EquipmentRead> {
        return this.service.equipmentUndoDelete({
            id,
        }) as Observable<EquipmentRead>;
    }
    protected getObs$(id: string): Observable<EquipmentRead> {
        return this.service
            .equipmentReadById({
                id,
            })
            .pipe(
                map(result => result as EquipmentRead),
                tap(result => {
                    this.addContainersToStoreIfNotPresent(
                        asArraySafe(result.containers)
                    );
                })
            );
    }
    protected updateObs$(
        id: string,
        operations: Operation[]
    ): Observable<EquipmentRead> {
        return this.service
            .equipmentUpdatePatch({
                id,
                operation: operations,
            })
            .pipe(
                map(result => result as EquipmentRead),
                tap(result => {
                    this.addContainersToStoreIfNotPresent(
                        asArraySafe(result.containers)
                    );
                })
            );
    }
    protected searchObs$(
        searchParams: EquipmentSearchParams
    ): Observable<IPagedResult<EquipmentRead>> {
        return this.service.equipmentSearch(searchParams).pipe(
            map(result => result as IPagedResult<EquipmentRead>),
            tap(result => {
                this.addContainersToStoreIfNotPresent(
                    result.data.flatMap(e => asArraySafe(e.containers))
                );
            })
        );
    }

    protected searchChangesObs$(
        searchParams: IChangesSearchParams
    ): Observable<IPagedResult<EntityChangeTrackingDto<EquipmentRead>>> {
        return this.service.equipmentGetChanges(searchParams) as Observable<
            IPagedResult<EntityChangeTrackingDto<EquipmentRead>>
        >;
    }

    private addContainersToStoreIfNotPresent(
        containers: ContainerViewModelRead[]
    ) {
        containers.forEach(container => {
            if (this.containerStore.get(container.id as string)) {
                return;
            }
            this.containerStore.add(container as ContainerRead, false);
        });
    }
}
